import React, { useMemo } from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { formatDateStr } from '../../config_and_helpers/helpers';
import S3MediaLink from '../Generic/S3MediaLink';

/* Read-only detail view for the internal worker timesheets dashboard. */

/**
 * @param {string|null|undefined} iso
 * @returns {string}
 */
function formatApiDate(iso) {
  if (!iso) return '';
  const datePart = String(iso).slice(0, 10);
  return formatDateStr(datePart);
}

/**
 * Sums six hour fields for a daily row (base, daycare, meeting, training,
 * variable, transport). Missing variable_hours counts as 0.
 *
 * @param {Object} row
 * @returns {number}
 */
function sumDayHours(row) {
  const a = [
    row.regular_hours,
    row.daycare_hours,
    row.meeting_hours,
    row.training_hours,
    row.variable_hours,
    row.transport_hours,
  ];
  return a.reduce((acc, v) => acc + (Number(v) || 0), 0);
}

/**
 * Renders read-only detail sections: header summary, daily hours, expenses, attachments.
 *
 * @param {Object} props
 * @param {Object} props.data - API detail payload
 * @returns {JSX.Element}
 */
const WorkerTimesheetDetailView = ({ data }) => {
  const dailyHours = useMemo(
    () => (Array.isArray(data?.daily_hours) ? data.daily_hours : []),
    [data?.daily_hours],
  );
  const expenses = Array.isArray(data.expenses) ? data.expenses : [];
  const attachments = Array.isArray(data.attachments) ? data.attachments : [];

  const columnTotals = useMemo(() => {
    const acc = {
      regular: 0,
      daycare: 0,
      meeting: 0,
      training: 0,
      variable: 0,
      transport: 0,
      grand: 0,
    };
    dailyHours.forEach(row => {
      const r = Number(row.regular_hours) || 0;
      const d = Number(row.daycare_hours) || 0;
      const m = Number(row.meeting_hours) || 0;
      const t = Number(row.training_hours) || 0;
      const vr = Number(row.variable_hours) || 0;
      const tp = Number(row.transport_hours) || 0;
      acc.regular += r;
      acc.daycare += d;
      acc.meeting += m;
      acc.training += t;
      acc.variable += vr;
      acc.transport += tp;
      acc.grand += r + d + m + t + vr + tp;
    });
    return acc;
  }, [dailyHours]);

  const monthLabel =
    data.month && typeof data.month === 'string' && data.month.length >= 7
      ? data.month.slice(0, 7)
      : data.month;

  const statusTotals = useMemo(
    () =>
      dailyHours.reduce(
        (acc, row) => {
          if (row?.is_sick_day) acc.sick += 1;
          if (row?.is_off_day) acc.off += 1;
          return acc;
        },
        { sick: 0, off: 0 },
      ),
    [dailyHours],
  );

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4} className="mb-2">
              <div className="text-muted small">עובד</div>
              <div className="fw-semibold" dir="auto">
                {data.first_name} {data.last_name}
              </div>
            </Col>
            <Col md={4} className="mb-2">
              <div className="text-muted small">תעודת זהות</div>
              <div className="text-end" dir="auto">
                {data.worker_reference_id || '—'}
              </div>
            </Col>
            <Col md={4} className="mb-2">
              <div className="text-muted small">חודש דיווח</div>
              <div>{monthLabel}</div>
            </Col>
            <Col md={4} className="mb-2">
              <div className="text-muted small">תאריך הגשה</div>
              <div className="small">
                {data.created_at ? formatApiDate(data.created_at) : '—'}
              </div>
            </Col>
            <Col md={12}>
              <div className="text-muted small">הערות</div>
              <div dir="auto">{data.notes || '—'}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h5 className="mt-4 mb-3">שעות יומיות</h5>
      <Table responsive striped bordered size="sm" className="mb-4">
        <thead>
          <tr>
            <th>תאריך</th>
            <th className="text-end">שעות בסיס</th>
            <th className="text-end">צהרון</th>
            <th className="text-end">ישיבה</th>
            <th className="text-end">השתלמות</th>
            <th className="text-end">שעות משתנות</th>
            <th className="text-end">שעות הובלה</th>
            <th className="text-center">יום מחלה</th>
            <th className="text-center">יום חופשה</th>
            <th className="text-center">אישור מחלה</th>
            <th className="text-end">סה״כ יומי</th>
          </tr>
        </thead>
        <tbody>
          {dailyHours.map(row => {
            const dayTot = sumDayHours(row);
            return (
              <tr key={row.id ?? row.work_date}>
                <td>{formatDateStr(row.work_date)}</td>
                <td className="text-end">{row.regular_hours}</td>
                <td className="text-end">{row.daycare_hours}</td>
                <td className="text-end">{row.meeting_hours}</td>
                <td className="text-end">{row.training_hours}</td>
                <td className="text-end">{row.variable_hours ?? '0.00'}</td>
                <td className="text-end">{row.transport_hours}</td>
                <td className="text-center">{row.is_sick_day ? 'כן' : '—'}</td>
                <td className="text-center">{row.is_off_day ? 'כן' : '—'}</td>
                <td className="text-center">
                  {row.is_sick_day && row.sick_note_url ? (
                    <a
                      href={row.sick_note_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      פתיחת אישור
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="text-end">{dayTot.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
        {dailyHours.length > 0 && (
          <tfoot className="table-secondary">
            <tr>
              <td className="fw-semibold">סיכומים</td>
              <td className="text-end fw-semibold">
                {columnTotals.regular.toFixed(2)}
              </td>
              <td className="text-end fw-semibold">
                {columnTotals.daycare.toFixed(2)}
              </td>
              <td className="text-end fw-semibold">
                {columnTotals.meeting.toFixed(2)}
              </td>
              <td className="text-end fw-semibold">
                {columnTotals.training.toFixed(2)}
              </td>
              <td className="text-end fw-semibold">
                {columnTotals.variable.toFixed(2)}
              </td>
              <td className="text-end fw-semibold">
                {columnTotals.transport.toFixed(2)}
              </td>
              <td className="text-center fw-semibold">{statusTotals.sick}</td>
              <td className="text-center fw-semibold">{statusTotals.off}</td>
              <td className="text-center fw-semibold">—</td>
              <td className="text-end fw-semibold">
                {columnTotals.grand.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        )}
      </Table>

      <h5 className="mt-4 mb-3">הוצאות</h5>
      <Table responsive striped bordered size="sm" className="mb-4">
        <thead>
          <tr>
            <th>תיאור</th>
            <th className="text-end">סכום</th>
            <th>קבלה</th>
          </tr>
        </thead>
        <tbody>
          {expenses.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-muted">
                אין הוצאות
              </td>
            </tr>
          ) : (
            expenses.map(ex => (
              <tr key={ex.id}>
                <td dir="auto">{ex.description}</td>
                <td className="text-end">{ex.amount}</td>
                <td>
                  {ex.receipt_url ? (
                    <S3MediaLink url={ex.receipt_url} title="Open receipt">
                      פתיחת קבלה
                    </S3MediaLink>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <h5 className="mt-4 mb-3">קבצים מצורפים</h5>
      <Table responsive striped bordered size="sm">
        <thead>
          <tr>
            <th>תיאור</th>
            <th>תאריך העלאה</th>
            <th>קובץ</th>
          </tr>
        </thead>
        <tbody>
          {attachments.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-muted">
                אין קבצים מצורפים
              </td>
            </tr>
          ) : (
            attachments.map(att => (
              <tr key={att.id}>
                <td dir="auto">{att.caption || '—'}</td>
                <td className="small">
                  {att.uploaded_at ? formatApiDate(att.uploaded_at) : '—'}
                </td>
                <td>
                  {att.file_url ? (
                    <S3MediaLink url={att.file_url} title="Open attachment">
                      פתיחת קובץ
                    </S3MediaLink>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  );
};

export default WorkerTimesheetDetailView;
